import React, { type FC, useCallback, useMemo } from "react";
import { useActionSheet, useIsOverlayOpenByKeys, useWindows } from "@app/overlay";
import confirm from "@app/services/confirm/confirm";
import { useBoAvOperations, type ApiOmpBoAvOperation } from "@src/api/bo";
import { CardHeader } from "@shared/ui/card-header";
import { CardLoader } from "@src/components/card-loader";
import {
    BusinessBoStateValue,
    BusinessDateValue,
    BusinessDivisionValue,
    formatBoStateText,
    formatDivisionText,
} from "@src/components/business-data-display";
import { useRequiredNumberParam } from "@src/hooks/useRequiredNumberParam";
import { useBoOperationActionItems } from "@src/businessModules/WorkShop/shared/bo/useBoOperationActionItems";
import { useBoOperationConfirm } from "@src/businessModules/WorkShop/shared/bo/useBoOperationConfirm";
import { DetailList } from "@shared/ui/detail-row";
import type { ActionSheetSection } from "@shared/ui/overlay/action-sheet";
import type { ApiStockLot } from "../../../../api/lots";
import type { StockGathering, StockGatheringReserveItem } from "../../../../api/stockGatherings";
import { StockGatheringCompletionReservesList } from "./components/StockGatheringCompletionReservesList/StockGatheringCompletionReservesList";
import { StockGatheringCompletionScanPlaceholder } from "./components/StockGatheringCompletionScanPlaceholder/StockGatheringCompletionScanPlaceholder";
import { useStockGatheringCompletionCardQuery, useStockGatheringCompletionReservesQuery } from "./hooks";
import { useStockGatheringCompletionCardMutations } from "./useStockGatheringCompletionCardMutations";
import { stockGatheringCompletionCardWindows } from "./stockGatheringCompletionCard.windows";
import "./StockGatheringCompletionCard.scss";

const STOCK_GATHERING_COMPLETION_CARD_SCAN_PAUSE_WINDOW_KEYS = [
    stockGatheringCompletionCardWindows.reserve.key,
    stockGatheringCompletionCardWindows.reservePick.key,
];

/** Карточка КВ для комплектования резервов. */
export const StockGatheringCompletionCard: FC = () => {
    const gatheringCode = useRequiredNumberParam("code", "код КВ");
    const windows = useWindows();
    const actionSheet = useActionSheet();

    const cardQuery = useStockGatheringCompletionCardQuery(gatheringCode);
    const reservesQuery = useStockGatheringCompletionReservesQuery(gatheringCode);
    const gathering = cardQuery.data;
    const avOperationsQuery = useBoAvOperations(gathering?.Code, gathering != null);
    const isCardScannerSuppressedByWindow = useIsOverlayOpenByKeys(STOCK_GATHERING_COMPLETION_CARD_SCAN_PAUSE_WINDOW_KEYS);

    const mutations = useStockGatheringCompletionCardMutations({
        onReserveRefetch: (params) => reservesQuery.refetchReserveByCode(params),
        onBoOperationSuccess: () => {
            void cardQuery.refetch();
            void reservesQuery.refetch();
            void avOperationsQuery.refetch();
        },
    });

    const { confirmAndPerform } = useBoOperationConfirm({
        perform: mutations.performBoOperationMutation.mutateAsync,
    });

    const buildBoOperationParams = useCallback((operation: ApiOmpBoAvOperation, currentGathering: StockGathering) => {
        return {
            operation,
            boCode: currentGathering.Code,
            documentLabel: `КВ №${currentGathering.Num}`,
            fields: [
                { label: "Отправитель", value: formatEmptyValue(formatDivisionText(currentGathering.SenderDivision)) },
                { label: "Получатель", value: formatEmptyValue(formatDivisionText(currentGathering.ReceiverDivision)) },
                { label: "Статус", value: formatEmptyValue(formatBoStateText(currentGathering.State)) },
            ],
        };
    }, []);

    const operationItems = useBoOperationActionItems({
        operations: avOperationsQuery.data,
        context: gathering,
        confirmAndPerform,
        buildParams: buildBoOperationParams,
    });

    const finishComplect = useCallback(
        async (closeActionSheet: () => void): Promise<void> => {
            if (gathering == null) {
                return;
            }

            closeActionSheet();

            const accepted = await confirm.show({
                title: "Завершить комплектование?",
                message: `Будут найдены ВЗН СУ по КВ №${gathering.Num} и переведены на статус из общих параметров СУ (вкладка ТСД).`,
                confirmText: "Завершить",
                cancelText: "Отмена",
            });

            if (!accepted) {
                return;
            }

            await mutations.finishComplectMutation.mutateAsync({ gatheringCode: gathering.Code });
        },
        [gathering, mutations.finishComplectMutation],
    );

    const actionSheetSections = useMemo<ActionSheetSection[]>(() => {
        const sections: ActionSheetSection[] = [
            {
                id: "complect",
                items: [
                    {
                        type: "action",
                        id: "finish-complect",
                        label: "Завершить комплектование",
                        description: "Перевести связанные ВЗН СУ на статус из параметров СУ",
                        loading: mutations.finishComplectMutation.isPending,
                        shouldCloseOnSelect: false,
                        onSelect: (sheet) => {
                            void finishComplect(sheet.close);
                        },
                    },
                ],
            },
        ];

        if (operationItems.length > 0) {
            sections.push({
                id: "bo",
                items: [
                    {
                        type: "drill-in",
                        id: "bo-operations",
                        label: "Выполнить операцию",
                        description: "Выбрать доступную БО-операцию над КВ",
                        sections: [{ id: "bo-operations", items: operationItems }],
                    },
                ],
            });
        }

        return sections;
    }, [finishComplect, mutations.finishComplectMutation.isPending, operationItems]);

    const openReserveWindow = useCallback(
        (reserveCode: number): void => {
            stockGatheringCompletionCardWindows.reserve.open(windows, {
                gatheringCode,
                reserveCode,
                reserves: reservesQuery.reserves,
                completeReserveMutation: mutations.completeReserveMutation,
            });
        },
        [gatheringCode, mutations.completeReserveMutation, reservesQuery.reserves, windows],
    );

    const openReservePickWindow = useCallback(
        (payload: { lot: ApiStockLot; reserves: StockGatheringReserveItem[] }): void => {
            const pickOverlay = stockGatheringCompletionCardWindows.reservePick.open(windows, {
                lot: payload.lot,
                reserves: payload.reserves,
                onSelect: (reserveCode) => {
                    openReserveWindow(reserveCode);
                    pickOverlay.close();
                },
            });
        },
        [openReserveWindow, windows],
    );

    if (cardQuery.isFetching || gathering == null) {
        return <CardLoader header list />;
    }

    return (
        <div className="stock_gathering_completion_card_page">
            <CardHeader
                text={`Комлектование по КВ №${gathering.Num}`}
                buttons={[
                    {
                        type: "icon",
                        text: "Действия",
                        srcIcon: "/images/common/more_vert.svg",
                        onClick: () => actionSheet.open({ title: "Действия", sections: actionSheetSections }),
                    },
                ]}
            />
            <DetailList
                className="stock_gathering_completion_card_inform"
                layout="tiles"
                columns="auto"
                minWidth={96}
                oneLine
            >
                <DetailList.Row label="Дата" value={<BusinessDateValue value={gathering.DocDate} />} />
                <DetailList.Row label="Статус КВ" value={<BusinessBoStateValue state={gathering.State} />} />
                <DetailList.Row
                    label="Отправитель"
                    value={
                        <BusinessDivisionValue division={gathering.SenderDivision} section={gathering.SenderSectionData} />
                    }
                />
                <DetailList.Row
                    label="Получатель"
                    value={
                        <BusinessDivisionValue
                            division={gathering.ReceiverDivision}
                            section={gathering.ReceiverSectionData}
                        />
                    }
                />
            </DetailList>
            <StockGatheringCompletionReservesList reservesQuery={reservesQuery} onOpenReserve={openReserveWindow} />
            <StockGatheringCompletionScanPlaceholder
                reserves={reservesQuery.reserves}
                enabled={!reservesQuery.isFetching && !isCardScannerSuppressedByWindow}
                onOpenReserve={openReserveWindow}
                onAmbiguousReserves={openReservePickWindow}
            />
        </div>
    );
};

/**
 * Нормализует пустые подписи справочных значений для confirm-диалогов.
 * @param value отформатированное display-значение
 */
function formatEmptyValue(value: string): string {
    return value === "" ? "—" : value;
}
